import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import {
  Scale,
  Shield,
  AlertTriangle,
  ChevronDown,
  ChevronRight,
  Loader2,
  Info,
  CheckCircle2,
  FileText,
  BookOpen,
  AlertCircle,
  Users,
  X
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ROLE_LABELS = {
  comprador: 'Comprador',
  vendedor: 'Vendedor',
  arrendatario: 'Arrendatario',
  arrendador: 'Arrendador',
  trabajador: 'Trabajador',
  empleador: 'Empleador',
  cliente: 'Cliente',
  prestador: 'Prestador de Servicios',
  deudor: 'Deudor',
  acreedor: 'Acreedor'
};

const IMPORTANCE_COLORS = {
  critica: 'bg-red-500/10 border-red-500/30 text-red-400',
  alta: 'bg-orange-500/10 border-orange-500/30 text-orange-400',
  media: 'bg-yellow-500/10 border-yellow-500/30 text-yellow-400',
  baja: 'bg-slate-500/10 border-slate-500/30 text-slate-400'
};

const IMPORTANCE_LABELS = {
  critica: 'Crítica',
  alta: 'Alta',
  media: 'Media',
  baja: 'Baja'
};

const ClauseCard = ({ clause, onSelect, isSelected }) => {
  const [expanded, setExpanded] = useState(false);
  
  return (
    <div 
      className={`border rounded-xl p-4 transition-all ${
        isSelected 
          ? 'border-blue-500 bg-blue-500/10' 
          : 'border-border bg-slate-900/50 hover:border-slate-700'
      }`}
    >
      <div className="flex items-start gap-3">
        <button
          onClick={() => onSelect(clause)}
          className={`mt-1 w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
            isSelected 
              ? 'bg-blue-500 border-blue-500' 
              : 'border-slate-600 hover:border-slate-400'
          }`}
        >
          {isSelected && <CheckCircle2 className="w-3 h-3 text-white" />}
        </button>
        
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-2">
            <h4 className="font-semibold">{clause.name}</h4>
            <span className={`text-xs px-2 py-0.5 rounded-full border ${IMPORTANCE_COLORS[clause.importance]}`}>
              {IMPORTANCE_LABELS[clause.importance]}
            </span>
          </div>
          
          <p className="text-sm text-slate-400 mb-2">{clause.description}</p>
          
          {clause.legal_basis && (
            <p className="text-xs text-slate-500 mb-2">
              <BookOpen className="w-3 h-3 inline mr-1" />
              {clause.legal_basis}
            </p>
          )}
          
          <button
            onClick={() => setExpanded(!expanded)}
            className="text-sm text-blue-400 hover:text-blue-300 flex items-center gap-1"
          >
            {expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
            {expanded ? 'Ocultar detalles' : 'Ver más detalles'}
          </button>
          
          {expanded && (
            <div className="mt-3 space-y-3">
              {clause.why_include && (
                <div className="p-3 bg-emerald-500/10 border border-emerald-500/20 rounded-lg">
                  <h5 className="text-sm font-medium text-emerald-400 mb-1 flex items-center gap-1">
                    <Info className="w-4 h-4" />
                    ¿Por qué incluir esta cláusula?
                  </h5>
                  <p className="text-sm text-slate-300">{clause.why_include}</p>
                </div>
              )}
              
              {clause.risk_if_missing && (
                <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
                  <h5 className="text-sm font-medium text-red-400 mb-1 flex items-center gap-1">
                    <AlertTriangle className="w-4 h-4" />
                    Riesgo si no se incluye
                  </h5>
                  <p className="text-sm text-slate-300">{clause.risk_if_missing}</p>
                </div>
              )}
              
              {clause.exceptions && clause.exceptions.length > 0 && (
                <div className="p-3 bg-yellow-500/10 border border-yellow-500/20 rounded-lg">
                  <h5 className="text-sm font-medium text-yellow-400 mb-1 flex items-center gap-1">
                    <AlertCircle className="w-4 h-4" />
                    Excepciones a considerar
                  </h5>
                  <ul className="text-sm text-slate-300 list-disc list-inside space-y-1">
                    {clause.exceptions.map((exc, idx) => (
                      <li key={idx}>{exc}</li>
                    ))}
                  </ul>
                </div>
              )}
              
              {clause.template && (
                <div className="p-3 bg-slate-800 rounded-lg">
                  <h5 className="text-sm font-medium text-slate-300 mb-2 flex items-center gap-1">
                    <FileText className="w-4 h-4" />
                    Texto modelo
                  </h5>
                  <p className="text-xs text-slate-400 font-mono whitespace-pre-wrap">
                    {clause.template}
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const ClauseAdvisorPanel = ({ contractType, onClose, onApplyClauses }) => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [clauses, setClauses] = useState(null);
  const [selectedClauses, setSelectedClauses] = useState([]);
  const [userRole, setUserRole] = useState('');
  const [protectionClauses, setProtectionClauses] = useState(null);
  const [activeTab, setActiveTab] = useState('all');
  
  useEffect(() => {
    fetchClauses();
  }, [contractType]);
  
  const fetchClauses = async () => {
    try {
      const response = await fetch(`${API_URL}/api/clauses/suggestions/${contractType}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setClauses(data);
    } catch (error) {
      console.error('Error fetching clauses:', error);
      toast.error('Error al cargar cláusulas');
    } finally {
      setLoading(false);
    }
  };
  
  const fetchProtectionClauses = async (role) => {
    try {
      const response = await fetch(`${API_URL}/api/clauses/protection`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          contract_type: contractType,
          user_role: role
        })
      });
      const data = await response.json();
      setProtectionClauses(data);
    } catch (error) {
      console.error('Error fetching protection clauses:', error);
    }
  };
  
  const handleRoleSelect = (role) => {
    setUserRole(role);
    fetchProtectionClauses(role);
    setActiveTab('protection');
  };
  
  const toggleClause = (clause) => {
    setSelectedClauses(prev => {
      const isSelected = prev.some(c => c.id === clause.id);
      if (isSelected) {
        return prev.filter(c => c.id !== clause.id);
      } else {
        return [...prev, clause];
      }
    });
  };
  
  const handleApply = () => {
    if (selectedClauses.length === 0) {
      toast.error('Selecciona al menos una cláusula');
      return;
    }
    onApplyClauses(selectedClauses);
    toast.success(`${selectedClauses.length} cláusula(s) agregada(s) al contrato`);
    onClose();
  };
  
  if (loading) {
    return (
      <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
        <div className="bg-card rounded-xl p-8 text-center">
          <Loader2 className="w-8 h-8 animate-spin mx-auto text-blue-500" />
          <p className="mt-4 text-slate-400">Cargando asesor de cláusulas...</p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-card border border-border rounded-2xl w-full max-w-4xl my-4 max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="p-4 border-b border-border flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-cyan-500 to-blue-500 rounded-xl flex items-center justify-center">
              <Scale className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-bold font-['Outfit']">Asesor de Cláusulas</h2>
              <p className="text-sm text-slate-400">
                Contrato: {clauses?.contract_type} | Base legal: {clauses?.legal_basis}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-800 rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>
        
        {/* Role Selection */}
        <div className="p-4 border-b border-border flex-shrink-0">
          <label className="text-sm font-medium text-slate-300 mb-2 block">
            ¿Cuál es tu rol en este contrato? (para recomendaciones personalizadas)
          </label>
          <div className="flex flex-wrap gap-2">
            {Object.entries(ROLE_LABELS).slice(0, 6).map(([role, label]) => (
              <button
                key={role}
                onClick={() => handleRoleSelect(role)}
                className={`px-3 py-1.5 rounded-lg text-sm transition-all ${
                  userRole === role
                    ? 'bg-blue-500 text-white'
                    : 'bg-slate-800 text-slate-300 hover:bg-slate-700'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
        
        {/* Tabs */}
        <div className="flex border-b border-border flex-shrink-0">
          <button
            onClick={() => setActiveTab('all')}
            className={`px-4 py-3 text-sm font-medium transition-colors ${
              activeTab === 'all'
                ? 'text-blue-400 border-b-2 border-blue-400'
                : 'text-slate-400 hover:text-white'
            }`}
          >
            Todas las Cláusulas ({clauses?.total_clauses || 0})
          </button>
          {userRole && (
            <button
              onClick={() => setActiveTab('protection')}
              className={`px-4 py-3 text-sm font-medium transition-colors ${
                activeTab === 'protection'
                  ? 'text-blue-400 border-b-2 border-blue-400'
                  : 'text-slate-400 hover:text-white'
              }`}
            >
              <Shield className="w-4 h-4 inline mr-1" />
              Protección para {ROLE_LABELS[userRole]} ({protectionClauses?.protection_clauses?.length || 0})
            </button>
          )}
        </div>
        
        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {activeTab === 'protection' && protectionClauses ? (
            <div className="space-y-4">
              {protectionClauses.recommendation_summary && (
                <div className="p-4 bg-blue-500/10 border border-blue-500/20 rounded-xl">
                  <h3 className="font-semibold text-blue-400 mb-2 flex items-center gap-2">
                    <Shield className="w-5 h-5" />
                    Recomendaciones para ti como {ROLE_LABELS[userRole]}
                  </h3>
                  <pre className="text-sm text-slate-300 whitespace-pre-wrap font-sans">
                    {protectionClauses.recommendation_summary}
                  </pre>
                </div>
              )}
              
              <h3 className="font-semibold text-slate-300">Cláusulas que te protegen:</h3>
              {protectionClauses.protection_clauses?.map((clause, idx) => (
                <ClauseCard
                  key={clause.id || idx}
                  clause={clause}
                  onSelect={toggleClause}
                  isSelected={selectedClauses.some(c => c.id === clause.id)}
                />
              ))}
              
              {protectionClauses.mutual_clauses?.length > 0 && (
                <>
                  <h3 className="font-semibold text-slate-300 mt-6 flex items-center gap-2">
                    <Users className="w-5 h-5" />
                    Cláusulas de protección mutua:
                  </h3>
                  {protectionClauses.mutual_clauses.map((clause, idx) => (
                    <ClauseCard
                      key={clause.id || idx}
                      clause={clause}
                      onSelect={toggleClause}
                      isSelected={selectedClauses.some(c => c.id === clause.id)}
                    />
                  ))}
                </>
              )}
            </div>
          ) : (
            <div className="space-y-6">
              {clauses?.clauses_by_importance && Object.entries(clauses.clauses_by_importance).map(([importance, clauseList]) => (
                clauseList.length > 0 && (
                  <div key={importance}>
                    <h3 className={`font-semibold mb-3 flex items-center gap-2 ${
                      importance === 'critica' ? 'text-red-400' :
                      importance === 'alta' ? 'text-orange-400' :
                      importance === 'media' ? 'text-yellow-400' : 'text-slate-400'
                    }`}>
                      {importance === 'critica' && <AlertTriangle className="w-5 h-5" />}
                      Importancia {IMPORTANCE_LABELS[importance]} ({clauseList.length})
                    </h3>
                    <div className="space-y-3">
                      {clauseList.map((clause, idx) => (
                        <ClauseCard
                          key={clause.id || idx}
                          clause={clause}
                          onSelect={toggleClause}
                          isSelected={selectedClauses.some(c => c.id === clause.id)}
                        />
                      ))}
                    </div>
                  </div>
                )
              ))}
            </div>
          )}
        </div>
        
        {/* Footer */}
        <div className="p-4 border-t border-border flex items-center justify-between flex-shrink-0">
          <div className="text-sm text-slate-400">
            {selectedClauses.length} cláusula(s) seleccionada(s)
          </div>
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-slate-400 hover:text-white transition-colors"
            >
              Cancelar
            </button>
            <button
              onClick={handleApply}
              disabled={selectedClauses.length === 0}
              className="px-4 py-2 bg-blue-500 hover:bg-blue-600 disabled:bg-slate-700 disabled:text-slate-500 rounded-lg font-medium transition-colors"
            >
              Aplicar Cláusulas Seleccionadas
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClauseAdvisorPanel;
